import type { DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers/models";

import { extractOmnisearchValue, useOmnisearchState } from "hooks/omnisearch-v2";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { addOrUpdateKeyInOmnisearch, removeKeyInOmnisearch } from "utils/omnisearchUtils";

import {
  PERIOD_ALIAS_KEY,
  PERIOD_FILTER_KEY,
  readPeriodFilters,
  writePeriodFilter,
} from "./periodFilter";

export const DEFAULT_PERIOD_MONTHS = 3;

function defaultStartDate(): DateTime {
  return DateTime.now().minus({ months: DEFAULT_PERIOD_MONTHS }).startOf("month");
}

function formatDateForApi(value: DateTime | null): string | undefined {
  return value?.isValid ? value.toFormat("yyyy-MM-dd") : undefined;
}

export function usePeriodFilter() {
  const { omnisearch, getOmnisearch, setOmnisearchImmediate } = useOmnisearchState();
  const defaultStart = useMemo(defaultStartDate, []);
  const periodValue = extractOmnisearchValue(omnisearch, PERIOD_FILTER_KEY);
  const aliasValue = extractOmnisearchValue(omnisearch, PERIOD_ALIAS_KEY);
  const [startDate, endDate] = useMemo(
    () => readPeriodFilters([periodValue, aliasValue], defaultStart),
    [periodValue, aliasValue, defaultStart]
  );

  const update = useCallback(
    (start: DateTime | null, end: DateTime | null) => {
      const search = removeKeyInOmnisearch(getOmnisearch(), PERIOD_ALIAS_KEY);
      setOmnisearchImmediate(
        addOrUpdateKeyInOmnisearch(search, PERIOD_FILTER_KEY, writePeriodFilter(start, end))
      );
    },
    [getOmnisearch, setOmnisearchImmediate]
  );
  const onStartChange = useCallback(
    (start: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (context.validationError != null || (start && !start.isValid)) return;
      update(start, endDate);
    },
    [endDate, update]
  );
  const onEndChange = useCallback(
    (end: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (context.validationError != null || (end && !end.isValid)) return;
      update(startDate, end);
    },
    [startDate, update]
  );

  return {
    startDate,
    endDate,
    startDateStr: formatDateForApi(startDate),
    endDateStr: formatDateForApi(endDate),
    onStartChange,
    onEndChange,
  };
}
